import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormSelect, Tag, Tooltip } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Eye, EyeClosed, GripHorizontal } from 'lucide-react';

export const FieldVisibilitySelector = ({
  availableFields,
  fieldsState,
  onChange,
  onDropDownStateChange,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localFieldsState, setLocalFieldsState] = useState(fieldsState || []);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dropdownRef = useRef(null);
  const dragItem = useRef();
  const dragOverItem = useRef();

  useEffect(() => {
    if (fieldsState && fieldsState.length > 0) {
      setLocalFieldsState(fieldsState);
    } else if (availableFields.length > 0) {
      setLocalFieldsState(availableFields.map((field) => ({ accessor: field.accessor, show: true })));
    }
  }, [fieldsState, availableFields]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (onDropDownStateChange) {
          onDropDownStateChange('close');
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onDropDownStateChange]);

  useEffect(() => {
    const handleDragEnd = () => {
      if (dragItem.current === null || dragOverItem.current === null) return;

      const fromIndex = dragItem.current;
      const toIndex = dragOverItem.current;

      const newFieldsState = [...localFieldsState];
      const draggedItemContent = newFieldsState.splice(fromIndex, 1)[0];
      newFieldsState.splice(toIndex, 0, draggedItemContent);

      dragItem.current = null;
      dragOverItem.current = null;

      setLocalFieldsState(newFieldsState);
      setDraggedItem(null);
      setDragOverIndex(null);

      onChange(newFieldsState);
    };

    document.addEventListener('dragend', handleDragEnd);
    return () => document.removeEventListener('dragend', handleDragEnd);
  }, [localFieldsState, onChange]);

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItem(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();

    if (index === draggedItem) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const elementTop = rect.top;
    const elementHeight = rect.height;
    const relativeY = mouseY - elementTop;

    const isTopHalf = relativeY < elementHeight / 2;

    if (isTopHalf) {
      dragOverItem.current = index;
      setDragOverIndex(index);
    } else {
      dragOverItem.current = index + 1;
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const toggleField = (fieldAccessor) => {
    const newFieldsState = localFieldsState.map((field) =>
      field.accessor === fieldAccessor ? { ...field, show: !field.show } : field,
    ); // eslint-disable-line function-paren-newline

    setLocalFieldsState(newFieldsState);
    onChange(newFieldsState);
  };

  const getFieldInfo = (accessor) => availableFields.find((f) => f.accessor === accessor);

  const visibleCount = localFieldsState.filter((f) => f.show).length;
  let displayValue;
  if (disabled) {
    displayValue = 'All fields visible';
  } else if (visibleCount === availableFields.length) {
    displayValue = 'All fields visible';
  } else {
    displayValue = `${visibleCount} visible field${visibleCount !== 1 ? 's' : ''}`;
  }

  const handleInputClick = () => {
    if (disabled) return;
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    if (onDropDownStateChange) {
      onDropDownStateChange(newIsOpen ? 'open' : 'close');
    }
  };

  const formSelectContent = (
    <Box
      sx={{
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      onClick={handleInputClick}
    >
      <Box
        sx={{
          '& input': {
            cursor: disabled ? 'not-allowed' : 'pointer !important',
            opacity: disabled ? 0.5 : 1,
          },
          pointerEvents: 'none',
        }}
      >
        <FormSelect
          label="Field visibility"
          helpText="Choose which fields visitors can see and in what order."
          options={[{ label: displayValue, value: displayValue }]}
          size="medium"
          isDisabled={disabled}
          field={{
            id: 'field-visibility',
            name: 'field-visibility',
            value: { label: displayValue, value: displayValue },
            onChange: () => {},
          }}
          menuIsOpen={false}
        />
      </Box>
    </Box>
  );

  return (
    <Box ref={dropdownRef} sx={{ position: 'relative' }}>
      {disabled ? (
        <Tooltip
          position="top"
          options={{ offset: [0, -44] }}
          content="Field visibility can't be customized in the cards layout. Select the table layout to manage visible fields."
          trigger={formSelectContent}
        />
      ) : (
        formSelectContent
      )}

      {isOpen && !disabled && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: '400px',
            overflowY: 'auto',
            backgroundColor: 'white',
            boxShadow: '0px 2px 32px 0px #1B24320F, 0px 1px 4px 0px #1B24320F',
            borderRadius: '8px',
            zIndex: 11000,
            py: '8px',
          }}
          onDragLeave={handleDragLeave}
        >
          {localFieldsState.map((fieldState, index) => {
            const fieldInfo = getFieldInfo(fieldState.accessor);
            if (!fieldInfo) return null;

            return (
              <Box key={fieldState.accessor} sx={{ position: 'relative' }}>
                <Flex
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '12px',
                    py: '4px',
                    cursor: 'pointer',
                    opacity: draggedItem === index ? 0.5 : 1,
                    transform: draggedItem === index ? 'scale(1.02)' : 'scale(1)',
                    transition: 'opacity 0.2s, transform 0.2s, background-color 0.2s',
                    bg: draggedItem === index ? 'primary.50' : 'neutral.0',
                    '&:hover': {
                      backgroundColor: draggedItem === index ? 'primary.100' : 'neutral.20',
                    },
                  }}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                >
                  <Flex alignItems="center" sx={{ gap: '8px', flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        cursor: 'grab',
                        '&:active': { cursor: 'grabbing' },
                        color: 'text.subtle',
                        display: 'flex',
                        alignItems: 'center',
                        flex: '0 0 auto',
                      }}
                    >
                      <GripHorizontal size={16} />
                    </Box>
                    <Text
                      fontSize="14px"
                      fontWeight="400"
                      color="text.main"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {fieldInfo.Header}
                    </Text>
                    {fieldInfo.customField && (
                      <Tag
                        sx={{
                          flex: '0 0 auto',
                          pl: '8px',
                          pr: '8px!important',
                          py: '4px',
                          mr: 0,
                          '&& > div': { fontSize: '10px', fontWeight: '500', textTransform: 'uppercase' },
                        }}
                      >
                        {fieldInfo.customField.sharedList?.id ? 'List-specific' : 'Global'}
                      </Tag>
                    )}
                  </Flex>
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleField(fieldState.accessor);
                    }}
                    sx={{
                      cursor: 'pointer',
                      color: fieldState.show ? 'primary.main' : 'text.subtle',
                      display: 'flex',
                      alignItems: 'center',
                      p: '4px',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: 'neutral.40',
                      },
                    }}
                  >
                    {fieldState.show ? <Eye size={18} /> : <EyeClosed size={18} />}
                  </Box>
                </Flex>
                {dragOverIndex === index && draggedItem !== index && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '12px',
                      right: '12px',
                      top: 0,
                      height: '2px',
                      backgroundColor: 'primary.main',
                      borderRadius: '1px',
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

FieldVisibilitySelector.propTypes = {
  availableFields: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string.isRequired,
      Header: PropTypes.string.isRequired,
    }),
  ).isRequired,
  fieldsState: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string.isRequired,
      show: PropTypes.bool.isRequired,
    }),
  ),
  onChange: PropTypes.func.isRequired,
  onDropDownStateChange: PropTypes.func,
  disabled: PropTypes.bool,
};

FieldVisibilitySelector.defaultProps = {
  fieldsState: [],
  onDropDownStateChange: null,
  disabled: false,
};
